import React, { MouseEvent, FC } from "react";
import { Trans } from "i18n";
import withPagination, { IWithPaginationProps } from "hoc/withPagination";
import SelectAllControl, {
  ISelectAllControlTexts,
} from "components/SelectAllControl/SelectAllControl";
import SelfTapesInboxRow from "src/app/pages/JobsPage/SelfTapeRequestNotificationsPage/SelfTapesInboxRow";
import {
  ISelfTapeRequestNotificationTableItem,
  SelfTapeRequestNotificationUuid,
} from "./SelfTapeRequestNotificationTable.models";
import "./SelfTapeRequestNotificationTable.scss";
import { getEmptySelfTapesFeedText } from "components/SelfTapeRequestNotificationTable/SelfTapeRequestNotificationTable.helpers";

export interface ISelfTapeRequestNotificationTableTexts {
  resultStats: string;
  selectAllControlTexts: ISelectAllControlTexts;
}

export interface ISelfTapeRequestNotificationTableProps
  extends IWithPaginationProps {
  texts: ISelfTapeRequestNotificationTableTexts;
  items: ISelfTapeRequestNotificationTableItem[];
  selectedItems: SelfTapeRequestNotificationUuid[];
  returnUri: string;
  deselectItems: (itemIds: SelfTapeRequestNotificationUuid[]) => void;
  selectItems: (itemIds: SelfTapeRequestNotificationUuid[]) => void;
  onItemClick: (item: ISelfTapeRequestNotificationTableItem) => void;
  onMarkAsRead: (itemIds: SelfTapeRequestNotificationUuid[]) => void;
  onMarkAsUnread: (itemIds: SelfTapeRequestNotificationUuid[]) => void;
}

export const SelfTapeRequestNotificationTable: FC<
  ISelfTapeRequestNotificationTableProps
> = ({
  items,
  selectedItems,
  texts,
  selectItems,
  deselectItems,
  onItemClick,
  onMarkAsRead,
  onMarkAsUnread,
  renderPagination,
  renderPaginationResultStats,
}) => {
  const handleSelect =
    (isSelected: boolean, id: SelfTapeRequestNotificationUuid) => () => {
      if (isSelected) {
        deselectItems([id]);
      } else {
        selectItems([id]);
      }
    };

  const handleClick =
    (item: ISelfTapeRequestNotificationTableItem) => (e: MouseEvent) => {
      if (e.ctrlKey || e.metaKey) return;

      e.preventDefault();
      onItemClick(item);
    };

  const markItemsAsRead = (itemIds: SelfTapeRequestNotificationUuid[]) => {
    onMarkAsRead(itemIds);
  };

  const markItemsAsUnread = (itemIds: SelfTapeRequestNotificationUuid[]) => {
    onMarkAsUnread(itemIds);
  };

  const navigateToBreakdown = (url: string | undefined) => (e: MouseEvent) => {
    if (e.button === 2) return;

    e.preventDefault();
    e.stopPropagation();
    window.open(url);
  };

  const { i18nKey, link } = getEmptySelfTapesFeedText();

  return (
    <section className="с-self-tape-request-notification-table">
      <div>
        <div className="с-self-tape-request-notification-table__top-bar">
          <div className="с-self-tape-request-notification-table__count-info">
            {renderPaginationResultStats()}
          </div>
          <div className="с-self-tape-request-notification-table__pagination">
            {renderPagination()}
          </div>
        </div>
        <SelectAllControl
          texts={texts.selectAllControlTexts}
          selectItems={selectItems}
          deselectItems={deselectItems}
          selectedItems={selectedItems}
          markItemsAsRead={markItemsAsRead}
          markItemsAsUnread={markItemsAsUnread}
          items={items}
        />
      </div>
      <table className="с-self-tape-request-notification-table__body">
        {items.length ? (
          items.map((item) => (
            <SelfTapesInboxRow
              key={item.id}
              sender={item.sender.name}
              isChecked={selectedItems.includes(item.id)}
              value={item.id}
              title={item.title}
              additionalInfo={item.additionalInfo}
              isRead={item.isRead}
              onClick={handleClick(item)}
              onSelect={handleSelect(selectedItems.includes(item.id), item.id)}
              tags={item.tags.map((tag) => (
                <a
                  key={tag.id}
                  href={tag.url}
                  onMouseDown={navigateToBreakdown(tag.url)}
                  tabIndex={0}
                >
                  {tag.name}
                </a>
              ))}
              labels={item.labels}
              isAccepted={item.isAccepted}
            />
          ))
        ) : (
          <p className="mt-10">
            <Trans
              i18nKey={i18nKey}
              components={{
                a: <a href={link} target="_blank" rel="noopener noreferrer" />,
              }}
            />
          </p>
        )}
      </table>
      <div className="с-self-tape-request-notification-table__bottom-bar">
        <div className="с-self-tape-request-notification-table__pagination">
          {renderPagination(true)}
        </div>
      </div>
    </section>
  );
};

export default withPagination(SelfTapeRequestNotificationTable);
